import type { PaymentMethod, QuoteInput, QuoteView } from '@khaddi/contracts';
import {
  Button,
  colors,
  Cup,
  Field,
  FooterBar,
  formatKm,
  formatVnd,
  Icon,
  Screen,
  Segmented,
  Spinner,
  Stack,
  Text,
  Toggle,
  TopBar,
  useAction,
  useLayout,
  useApi,
  useRouter,
  useSession,
} from '@khaddi/ui';
import { useEffect, useMemo, useState } from 'react';
import { Pressable, TextInput, View } from 'react-native';
import { InlineError, ListRow, PaymentLogo, Section } from '../components/bits';
import { useTabNav } from '../components/TabNav';
import { CHANNEL_OPTIONS, toLineInput, useCart, type CartLine } from '../state/cart';
import { useCatalog } from '../state/catalog';
import { openPayment, rememberPaymentUrl } from '../state/payments';

const QUOTE_DEBOUNCE_MS = 400;
const PHONE = /^0\d{9}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAY_VERB: Record<PaymentMethod, string> = {
  vnpay: 'Thanh toán qua VNPay',
  momo: 'Thanh toán qua MoMo',
  zalopay: 'Thanh toán qua ZaloPay',
  card: 'Thanh toán bằng thẻ',
  cod: 'Đặt hàng · trả khi nhận',
  cash: 'Đặt hàng · trả tại quầy',
};

/** The API takes `0401234567` or `0401234567-001`; customers usually type the 13 digits straight. */
const normaliseTaxCode = (raw: string): string | null => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 13) return `${digits.slice(0, 10)}-${digits.slice(10)}`;
  return null;
};

/** Re-quotes shortly after the cart or options settle; stale responses are dropped. */
const useQuote = (input: QuoteInput | null) => {
  const api = useApi();
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const key = input ? JSON.stringify(input) : '';

  useEffect(() => {
    if (!input) return;
    let live = true;
    setPending(true);
    const timer = setTimeout(() => {
      api.orders
        .quote(input)
        .then((q) => {
          if (!live) return;
          setQuote(q);
          setError(null);
        })
        .catch((e: unknown) => {
          if (live) setError(e instanceof Error ? e.message : 'Không tính được giá');
        })
        .finally(() => {
          if (live) setPending(false);
        });
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // `key` captures every field of `input`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key]);

  return { quote, error, pending };
};

export const CheckoutScreen = () => {
  const api = useApi();
  const { user } = useSession();
  const { navigate, back } = useRouter();
  const cart = useCart();
  const { branch } = useCatalog();
  const { wide } = useLayout();
  const nav = useTabNav('menu', { wideOnly: true });
  const isDelivery = cart.channel === 'app_delivery';

  const [editingAddress, setEditingAddress] = useState(() => !cart.delivery.address);
  const [usePoints, setUsePoints] = useState(false);
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [taxCode, setTaxCode] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState(user?.email ?? '');
  const [voucherDraft, setVoucherDraft] = useState('');
  const [distanceText, setDistanceText] = useState(String(cart.delivery.distanceKm).replace('.', ','));
  const [showErrors, setShowErrors] = useState(false);

  // Prefill the recipient from the account the first time.
  useEffect(() => {
    if (!user) return;
    if (!cart.delivery.recipientName && !cart.delivery.phone) cart.updateDelivery({ recipientName: user.name, phone: user.phone ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [balance, setBalance] = useState(0);
  const quoteInput = useMemo<QuoteInput | null>(() => {
    if (!branch || cart.lines.length === 0) return null;
    return {
      branchId: branch.id,
      channel: cart.channel,
      lines: cart.lines.map(toLineInput),
      distanceKm: isDelivery ? cart.delivery.distanceKm : undefined,
      // The API caps redemption at the allowed maximum, so asking for the whole balance is safe.
      usePoints: usePoints ? balance : 0,
      voucherCode: cart.voucherCode ?? undefined,
    };
  }, [branch, cart.lines, cart.channel, cart.delivery.distanceKm, cart.voucherCode, isDelivery, usePoints, balance]);
  const { quote, error: quoteError, pending: quoting } = useQuote(quoteInput);

  useEffect(() => {
    if (quote) setBalance(quote.pointsBalance);
  }, [quote]);

  const enabledMethods = useMemo(() => (quote?.paymentMethods ?? []).filter((m) => m.enabled), [quote]);
  const chosen = enabledMethods.find((m) => m.method === method) ?? enabledMethods[0] ?? null;

  const voucher = useAction(async (code: string) => {
    if (!quoteInput) return;
    await api.orders.quote({ ...quoteInput, voucherCode: code });
    cart.setVoucherCode(code);
    setVoucherDraft('');
  });

  const delivery = cart.delivery;
  const deliveryErrors = {
    recipientName: delivery.recipientName.trim() ? null : 'Nhập tên người nhận',
    phone: PHONE.test(delivery.phone) ? null : 'Số điện thoại gồm 10 số, bắt đầu bằng 0',
    address: delivery.address.trim().length >= 5 ? null : 'Nhập địa chỉ giao hàng',
    distance: delivery.distanceKm > 0 && delivery.distanceKm <= 50 ? null : 'Khoảng cách từ 0,1 đến 50 km',
  };
  const normalisedTax = normaliseTaxCode(taxCode);
  const invoiceErrors = {
    taxCode: normalisedTax ? null : 'MST gồm 10 hoặc 13 số',
    email: EMAIL.test(invoiceEmail.trim()) ? null : 'Email chưa đúng',
  };
  const deliveryValid = !isDelivery || Object.values(deliveryErrors).every((e) => e === null);
  const invoiceValid = !wantsInvoice || Object.values(invoiceErrors).every((e) => e === null);

  const place = useAction(async () => {
    if (!quoteInput || !chosen) return;
    const result = await api.orders.place({
      ...quoteInput,
      paymentMethod: chosen.method,
      delivery: isDelivery ? { ...delivery, recipientName: delivery.recipientName.trim(), address: delivery.address.trim(), note: delivery.note.trim() } : null,
      invoice: wantsInvoice && normalisedTax ? { taxCode: normalisedTax, email: invoiceEmail.trim() } : null,
    });
    cart.clearLines();
    if (result.paymentUrl) {
      rememberPaymentUrl(result.order.id, result.paymentUrl);
      openPayment(result.paymentUrl, result.order.id);
      return;
    }
    navigate(`/orders/${encodeURIComponent(result.order.id)}`, { replace: true });
  });

  const submit = () => {
    setShowErrors(true);
    if (!deliveryValid) {
      setEditingAddress(true);
      return;
    }
    if (!invoiceValid) return;
    void place.run();
  };

  const setDistance = (text: string) => {
    setDistanceText(text);
    const km = Number(text.replace(',', '.'));
    if (Number.isFinite(km) && km > 0) cart.updateDelivery({ distanceKm: Math.round(km * 10) / 10 });
  };

  if (cart.lines.length === 0) {
    return (
      <Screen nav={nav}>
        <TopBar title="Giỏ hàng" onBack={() => back('/')} />
        <View style={{ padding: 24, alignItems: 'center', gap: 12, width: '100%', maxWidth: 480, alignSelf: 'center' }}>
          <Cup tint={colors.sky} width={96} />
          <Text variant="title">Giỏ hàng đang trống</Text>
          <Text variant="caption" style={{ textAlign: 'center' }}>
            Chọn một ly trà sữa Hồng Kông để bắt đầu nhé.
          </Text>
          <Button label="Xem thực đơn" onPress={() => navigate('/menu')} style={{ alignSelf: 'stretch' }} />
        </View>
      </Screen>
    );
  }

  const pricing = quote?.pricing;
  const eta = quote?.etaMinutes;
  const pointsRedeemable = quote?.maxRedeemablePoints ?? 0;

  const channelPicker = cart.channels.length > 1 ? (
    <Segmented label="Hình thức nhận món" options={CHANNEL_OPTIONS} value={cart.channel} onChange={cart.setChannel} track={colors.border} />
  ) : null;
  const whereSection = (
    <Section style={{ gap: 12 }}>
      <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
        <View style={{ width: 36, height: 36, borderRadius: 12, backgroundColor: colors.surfaceMuted, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={isDelivery ? 'pin' : 'store'} size={20} color={colors.primaryText} />
        </View>
        <View style={{ flex: 1, gap: 2 }}>
          <Text variant="caption">
            {isDelivery ? 'Giao đến' : 'Order tại quán · nhận món tại quầy'}
            {eta ? ` · dự kiến ${eta}–${eta + 5} phút` : ''}
          </Text>
          <Text variant="bodyStrong">{isDelivery ? delivery.address || 'Chưa có địa chỉ giao hàng' : branch?.name ?? ''}</Text>
          <Text variant="caption">
            {isDelivery ? `Giao từ ${branch?.name ?? ''} · ${formatKm(delivery.distanceKm)}` : branch?.address ?? ''}
          </Text>
        </View>
        {isDelivery ? (
          <Pressable accessibilityRole="button" accessibilityState={{ expanded: editingAddress }} onPress={() => setEditingAddress((v) => !v)} style={{ minHeight: 44, minWidth: 44, alignItems: 'flex-end', paddingTop: 4 }}>
            <Text variant="bodyStrong" color={colors.primaryText} style={{ fontSize: 13 }}>
              {editingAddress ? 'Xong' : 'Đổi'}
            </Text>
          </Pressable>
        ) : null}
      </View>
      {isDelivery && editingAddress ? (
        <Stack gap={10}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Field label="Người nhận" value={delivery.recipientName} onChangeText={(t) => cart.updateDelivery({ recipientName: t })} autoComplete="name" maxLength={80} error={showErrors ? deliveryErrors.recipientName : null} />
            <Field
              label="Số điện thoại"
              value={delivery.phone}
              onChangeText={(t) => cart.updateDelivery({ phone: t.replace(/\D/g, '') })}
              keyboardType="phone-pad"
              inputMode="tel"
              maxLength={10}
              error={showErrors ? deliveryErrors.phone : null}
            />
          </View>
          <Field
            label="Địa chỉ giao hàng"
            placeholder="Số nhà, đường, phường, quận"
            value={delivery.address}
            onChangeText={(t) => cart.updateDelivery({ address: t })}
            autoComplete="street-address"
            maxLength={200}
            error={showErrors ? deliveryErrors.address : null}
          />
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
            <View style={{ width: 120 }}>
              <Field label="Cách quán (km)" value={distanceText} onChangeText={setDistance} keyboardType="decimal-pad" inputMode="decimal" maxLength={4} error={showErrors ? deliveryErrors.distance : null} />
            </View>
            <Field label="Ghi chú cho shipper" placeholder="VD: gọi trước khi đến" value={delivery.note} onChangeText={(t) => cart.updateDelivery({ note: t })} maxLength={200} />
          </View>
          <Text variant="label" style={{ fontWeight: '400' }}>
            Ứng dụng chưa có bản đồ: ước lượng khoảng cách từ quán đến bạn để tính phí giao. Quán sẽ gọi xác nhận nếu chênh lệch nhiều.
          </Text>
        </Stack>
      ) : null}
    </Section>
  );
  const linesSection = (
    <Section style={{ paddingVertical: 6 }}>
      {cart.lines.map((line, index) => (
        <CartLineRow
          key={line.key}
          line={line}
          priced={matchingQuoteLine(quote, index, line)}
          onQuantity={(q) => cart.setQuantity(line.key, q)}
          onRemove={() => cart.removeLine(line.key)}
        />
      ))}
      <Pressable accessibilityRole="link" onPress={() => navigate('/menu')} style={{ minHeight: 44, justifyContent: 'center' }}>
        <Text variant="bodyStrong" color={colors.primaryText} style={{ fontSize: 13 }}>
          + Thêm món
        </Text>
      </Pressable>
    </Section>
  );
  const voucherSection = (
    <Section style={{ gap: 8 }}>
      {cart.voucherCode ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <Icon name="ticket" size={22} color={colors.green} />
          <View style={{ flex: 1 }}>
            <Text style={{ fontWeight: '600' }}>Mã {cart.voucherCode}</Text>
            <Text variant="label" style={{ fontWeight: '400' }}>
              {pricing?.promotionName ? `Đang áp dụng · giảm ${formatVnd(pricing.promotionDiscount)}` : 'Đang kiểm tra mã…'}
            </Text>
          </View>
          <Button label="Bỏ mã" variant="ghost" size="md" onPress={() => cart.setVoucherCode(null)} />
        </View>
      ) : (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Icon name="ticket" size={22} color={colors.primaryText} />
          <TextInput
            accessibilityLabel="Mã giảm giá"
            placeholder="Nhập mã giảm giá"
            placeholderTextColor={colors.textSubtle}
            autoCapitalize="characters"
            maxLength={20}
            value={voucherDraft}
            onChangeText={(t) => setVoucherDraft(t.toUpperCase().replace(/\s/g, ''))}
            onSubmitEditing={() => voucherDraft && void voucher.run(voucherDraft)}
            style={{ flex: 1, height: 44, borderWidth: 1, borderColor: colors.borderStrong, borderRadius: 10, paddingHorizontal: 10, fontSize: 14, color: colors.text, fontFamily: "'Be Vietnam Pro', sans-serif" }}
          />
          <Button label="Áp dụng" size="md" variant="outline" disabled={!voucherDraft} loading={voucher.pending} onPress={() => void voucher.run(voucherDraft)} style={{ paddingHorizontal: 14 }} />
        </View>
      )}
      <InlineError message={voucher.error} />
    </Section>
  );
  const paymentSection = (
    <Section style={{ gap: 10 }}>
      <Text variant="heading">Phương thức thanh toán</Text>
      {!quote && quoting ? <Spinner /> : null}
      <View accessibilityRole="radiogroup" aria-label="Phương thức thanh toán" style={{ gap: 10 }}>
        {enabledMethods.map((m) => {
          const on = chosen?.method === m.method;
          return (
            <Pressable
              key={m.method}
              accessibilityRole="radio"
              accessibilityState={{ checked: on }}
              onPress={() => setMethod(m.method)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 44, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 12, borderWidth: 1.5, borderColor: on ? colors.primaryText : '#E3EDF3', backgroundColor: on ? colors.surfaceMuted : colors.surface }}
            >
              <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: on ? 5 : 1.5, borderColor: on ? colors.primaryText : colors.inputBorder, backgroundColor: '#FFFFFF' }} />
              <PaymentLogo abbr={m.abbr} color={m.color} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '600' }}>{m.name}</Text>
                <Text variant="label" style={{ fontWeight: '400' }}>
                  {m.sub}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    </Section>
  );
  const extrasSection = (
    <Section style={{ paddingVertical: 4 }}>
      <ListRow
        icon="star"
        iconColor={colors.goldDeep}
        title={usePoints && pricing?.pointsRedeemed ? `Dùng ${pricing.pointsRedeemed.toLocaleString('vi-VN')} điểm` : `Dùng ${pointsRedeemable.toLocaleString('vi-VN')} điểm`}
        subtitle={
          quote
            ? pointsRedeemable > 0
              ? `Đang có ${quote.pointsBalance.toLocaleString('vi-VN')} điểm · giảm ${formatVnd((usePoints && pricing ? pricing.pointsRedeemed : pointsRedeemable) * quote.redeemValuePerPoint)}`
              : `Đang có ${quote.pointsBalance.toLocaleString('vi-VN')} điểm · chưa đủ để đổi`
            : 'Đang tính…'
        }
        trailing={pointsRedeemable > 0 || usePoints ? <Toggle label="Dùng điểm thành viên" value={usePoints} onChange={setUsePoints} /> : null}
      />
      <ListRow
        icon="receipt"
        title="Xuất hóa đơn điện tử"
        subtitle="Gửi về email · cá nhân hoặc công ty (MST)"
        last={!wantsInvoice}
        trailing={<Toggle label="Xuất hóa đơn điện tử" value={wantsInvoice} onChange={setWantsInvoice} />}
      />
      {wantsInvoice ? (
        <View style={{ flexDirection: 'row', gap: 8, paddingTop: 10, paddingBottom: 12 }}>
          <Field
            label="Mã số thuế"
            placeholder="0401xxxxxx"
            value={taxCode}
            onChangeText={(t) => setTaxCode(t.replace(/[^\d-]/g, ''))}
            inputMode="numeric"
            maxLength={14}
            style={{ height: 40, fontSize: 13 }}
            error={showErrors || taxCode.length >= 10 ? invoiceErrors.taxCode : null}
          />
          <Field
            label="Email nhận HĐ"
            placeholder="ten@congty.vn"
            value={invoiceEmail}
            onChangeText={setInvoiceEmail}
            keyboardType="email-address"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            style={{ height: 40, fontSize: 13 }}
            error={showErrors ? invoiceErrors.email : null}
          />
        </View>
      ) : null}
    </Section>
  );
  const totalsSection = (
    <Section style={{ gap: 8 }}>
      {quoteError ? (
        <Stack gap={8}>
          <InlineError message={quoteError} />
          {cart.voucherCode ? <Button label="Bỏ mã giảm giá" size="md" variant="outline" onPress={() => cart.setVoucherCode(null)} /> : null}
        </Stack>
      ) : null}
      {pricing ? (
        <>
          <TotalRow label="Tạm tính" value={formatVnd(pricing.subtotal)} />
          {isDelivery ? <TotalRow label="Phí giao hàng" value={pricing.deliveryFee ? formatVnd(pricing.deliveryFee) : 'Miễn phí'} /> : null}
          {pricing.promotionDiscount ? <TotalRow label={pricing.promotionName ? `Khuyến mãi · ${pricing.promotionName}` : 'Khuyến mãi'} value={`−${formatVnd(pricing.promotionDiscount)}`} positive /> : null}
          {pricing.pointsDiscount ? <TotalRow label={`Đổi ${pricing.pointsRedeemed.toLocaleString('vi-VN')} điểm`} value={`−${formatVnd(pricing.pointsDiscount)}`} positive /> : null}
        </>
      ) : !quoteError ? (
        <Spinner />
      ) : null}
    </Section>
  );
  // The pay button sticks to the bottom on phones and closes the summary column on wide screens.
  const payBar = (
    <FooterBar style={wide ? { borderRadius: 18, borderTopWidth: 0, paddingBottom: 14 } : null}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text variant="caption" style={{ fontSize: 13 }}>
          Tổng cộng
          {pricing ? (
            <>
              {' · '}
              <Text variant="caption" color={colors.goldText} style={{ fontSize: 13, fontWeight: '700' }}>
                +{pricing.pointsToEarn} điểm
              </Text>
            </>
          ) : null}
        </Text>
        <Text variant="title" numeric style={{ fontSize: 24, opacity: quoting ? 0.5 : 1 }}>
          {pricing ? formatVnd(pricing.total) : '—'}
        </Text>
      </View>
      <InlineError message={place.error} />
      <Button
        label={chosen ? PAY_VERB[chosen.method] : 'Đặt hàng'}
        icon="lock"
        onPress={submit}
        loading={place.pending}
        disabled={!quote || !chosen || quoting || Boolean(quoteError)}
      />
    </FooterBar>
  );

  if (!wide) {
    return (
      <Screen footer={payBar} nav={nav}>
        <TopBar title="Xác nhận đơn hàng" onBack={() => back('/')} />
        <View style={{ paddingTop: 12, paddingHorizontal: 16, gap: 12 }}>
          {channelPicker}
          {whereSection}
          {linesSection}
          {voucherSection}
          {paymentSection}
          {extrasSection}
          {totalsSection}
        </View>
      </Screen>
    );
  }
  return (
    <Screen nav={nav}>
      <TopBar title="Xác nhận đơn hàng" onBack={() => back('/')} />
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 20, padding: 20 }}>
        <View style={{ flex: 3, minWidth: 0, gap: 12 }}>
          {channelPicker}
          {whereSection}
          {paymentSection}
          {extrasSection}
        </View>
        <View style={{ flex: 2, minWidth: 0, gap: 12, position: 'sticky' as unknown as 'relative', top: 20 }}>
          {linesSection}
          {voucherSection}
          {totalsSection}
          {payBar}
        </View>
      </View>
    </Screen>
  );
};

/** Quotes are debounced, so a line is only priced from the quote once the quote has caught up with it. */
const matchingQuoteLine = (quote: QuoteView | null, index: number, line: CartLine) => {
  const priced = quote?.lines[index];
  return priced && priced.productId === line.productId && priced.quantity === line.quantity ? priced : undefined;
};

const TotalRow = ({ label, value, positive }: { label: string; value: string; positive?: boolean }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
    <Text color={colors.textMuted} style={{ fontWeight: '400', flex: 1 }}>
      {label}
    </Text>
    <Text color={positive ? colors.green : colors.text} numeric style={{ fontWeight: '400' }}>
      {value}
    </Text>
  </View>
);

const MiniButton = ({ icon, label, onPress }: { icon: 'plus' | 'minus' | 'trash'; label: string; onPress: () => void }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    onPress={onPress}
    hitSlop={6}
    style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: colors.background, alignItems: 'center', justifyContent: 'center' }}
  >
    <Icon name={icon} size={16} color={icon === 'trash' ? colors.danger : colors.text} strokeWidth={2.4} />
  </Pressable>
);

const CartLineRow = ({
  line,
  priced,
  onQuantity,
  onRemove,
}: {
  line: CartLine;
  priced: QuoteView['lines'][number] | undefined;
  onQuantity: (quantity: number) => void;
  onRemove: () => void;
}) => (
  <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: colors.divider }}>
    <View style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: line.background, alignItems: 'center', justifyContent: 'flex-end' }}>
      <Cup tint={line.tint} width={30} variant="thumb" />
    </View>
    <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
      <View>
        <Text variant="bodyStrong" numberOfLines={1}>
          {line.quantity}× {priced?.productName ?? line.name}
        </Text>
        <Text variant="caption" numberOfLines={2}>
          {priced?.optionsLabel ?? line.optionsLabel}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {line.quantity > 1 ? (
          <MiniButton icon="minus" label={`Bớt một ${line.name}`} onPress={() => onQuantity(line.quantity - 1)} />
        ) : (
          <MiniButton icon="trash" label={`Xóa ${line.name}`} onPress={onRemove} />
        )}
        <Text variant="bodyStrong" numeric style={{ minWidth: 18, textAlign: 'center' }}>
          {line.quantity}
        </Text>
        <MiniButton icon="plus" label={`Thêm một ${line.name}`} onPress={() => onQuantity(line.quantity + 1)} />
      </View>
    </View>
    <Text variant="bodyStrong" numeric>
      {formatVnd(priced?.lineTotal ?? line.unitPrice * line.quantity)}
    </Text>
  </View>
);
